import { StaffEmail } from "./StaffEmail";
import { PasskeySettings } from "./PasskeySettings";
import {
  startAuthentication,
  browserSupportsWebAuthn,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { MfaSettings } from "./MfaSettings";
import { AdminNavigation, adminNavItems } from "./AdminNavigation";
import { AccountNavigation } from "./AccountNavigation";
import { CustomerAccountHome } from "./CustomerAccountHome";
import { CustomerDirectory } from "./CustomerDirectory";
import { AccountConnectionOverview } from "./AccountConnectionOverview";
import {
  publicSite,
  customerSite,
  staffSite,
  isCacheRiverHost,
  serviceLink,
} from "./site-domains";
import { useLandingMotion } from "./useLandingMotion";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { AccountMessagingPanel } from "./AccountMessagingPanel";
import { PortalAccessPanel } from "./PortalAccessPanel";
import { ServiceStatusPanel, boundedStatusRequest } from "./ServiceStatus";
import { AdminDashboard } from "./AdminDashboard";
import { TimeTemperature } from "./TimeTemperature";
import { Experiences, AccountExperiences } from "./Experiences";
import { IVRPanel } from "./IVRPanel";
import { FeaturesPanel } from "./FeaturesPanel";
import { FeatureList } from "./FeatureList";
import { VoicemailPanel } from "./VoicemailPanel";
import { EmergencyCheckPanel } from "./EmergencyCheckPanel";
import { ExtensionsPanel } from "./ExtensionsPanel";
import { ServiceConnectionsPanel } from "./ServiceConnections";
import {
  useAccountConnections,
  type ConnectionFocus,
} from "./AccountConnections";
import {
  EquipmentPanel,
  InventoryForm,
  LocationForm,
  LocationCorrectionForm,
  NumbersPanel,
  PortQueue,
} from "./ServiceTools";
import { accountRequestGate } from "./account-request-gate";
import { CarriersPanel } from "./CarriersPanel";
import { AdminNumberPurchasePanel } from "./NumberPurchasePanel";
import { StripeConnectionPanel } from "./StripeConnectionPanel";
import { OwnedNumbersPanel } from "./OwnedNumbersPanel";
import { PlansPanel } from "./PlansPanel";
import { AccountPlanPanel } from "./AccountPlanPanel";
import { AccountStatusPanel, accountStatusLabel } from "./AccountStatusPanel";
import { SetupChecklist } from "./SetupChecklist";
import { AccountBillingArrangement } from "./AccountBillingArrangement";
import {
  AccountServiceSummary,
  AccountBillingSummary,
  setupNumberMessage,
  type AccountSummaryData,
} from "./AccountServiceSummary";
import { NewCustomerForm } from "./NewCustomerForm";
import type { PortalNumber } from "./owned-numbers";
import { sessionBoundary, sessionExpiredEvent } from "./session-boundary";
import { PortalTour } from "./PortalTour";
import {
  accountTabs,
  portalRouteUrl,
  readPortalRoute,
  refreshOverviewOnNavigate,
  type AccountTab,
  type PortalRoute,
} from "./portal-navigation";

type User = {
  id: string;
  email: string;
  displayName: string;
  kind: "admin" | "customer";
};
type Session = {
  user: User | null;
  csrfToken?: string;
  customerView?: {
    id: string;
    accountId: string;
    accountName: string;
    expiresAt: string;
    expired: boolean;
    readOnly: true;
  } | null;
};
type Row = Record<string, any>;
type Workspace = {
  registration?: Row | null;
  onboardingSteps?: Row[];
  account: Row & AccountSummaryData["account"];
  locations: (Row & AccountSummaryData["locations"][number])[];
  subscriptions: (Row & AccountSummaryData["subscriptions"][number])[];
  users: Row[];
  lines: (Row & AccountSummaryData["lines"][number])[];
  numbers: (Row & AccountSummaryData["numbers"][number])[];
  devices: (Row & AccountSummaryData["devices"][number])[];
  routes: Row[];
  orders: Row[];
  invoices: Row[];
  audit: Row[];
  setup?: {
    number_request: {
      mode: "new" | "existing" | "later";
      number?: string;
      carrier?: string;
    };
    created_at: string;
  } | null;
};
type Overview = {
  pagination?: {
    page: number;
    pageSize: number;
    hasMore: Record<string, boolean>;
  };
  accounts: Row[];
  requests: Row[];
  tickets: Row[];
  plans: Row[];
  numbers: Row[];
  devices: Row[];
  orders: Row[];
  invoices: Row[];
};
const hostedCopy = location.hostname.endsWith(".chatgpt.site");
const portalOrigin = hostedCopy ? publicSite : "";
const apiBase = `${portalOrigin}/v1/launch`;
const portalLink = (path: string) => serviceLink(path, location.hostname);
const money = (value: unknown) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(value ?? 0) / 100,
  );
const date = (value: unknown) =>
  value
    ? new Date(String(value)).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Not recorded";
const label = (value: unknown) =>
  String(value ?? "Not recorded").replaceAll("_", " ");

async function request<T>(
  path: string,
  options: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  const started = sessionBoundary.current();
  let response: Response;
  try {
    response = await fetch(`${apiBase}${path}`, {
      method: options.method ?? "GET",
      credentials: hostedCopy ? "omit" : "same-origin",
      ...(options.signal ? { signal: options.signal } : {}),
      headers: {
        "Content-Type": "application/json",
        ...(options.csrf ? { "X-CR-CSRF": options.csrf } : {}),
      },
      ...(options.body !== undefined
        ? { body: JSON.stringify(options.body) }
        : {}),
    });
  } catch {
    throw new Error("We couldn’t reach the service. Please try again.");
  }
  const body = await response.json().catch(() => null);
  if (sessionBoundary.shouldExpire(path, response.status, started))
    window.dispatchEvent(new Event(sessionExpiredEvent));
  if (!response.ok || !body)
    throw Object.assign(
      new Error(
        body?.detail ??
          "The service is temporarily unavailable. Please try again.",
      ),
      { status: response.status },
    );
  return body as T;
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <a
      className={`cr-brand ${compact ? "compact" : ""}`}
      href={portalLink("/")}
      aria-label="Cache River Telephone Company home"
    >
      <img
        src="/brand/cache-river-emblem-v6.png?v=66559bc5"
        width="80"
        height="80"
        alt=""
      />
      <span>
        <strong>Cache River</strong>
        <small>Telephone Company</small>
      </span>
    </a>
  );
}
function Field({ title, children }: { title: string; children: ReactNode }) {
  return (
    <label className="cr-field">
      <span>{title}</span>
      {children}
    </label>
  );
}
function Notice({
  children,
  error = false,
}: {
  children: ReactNode;
  error?: boolean;
}) {
  return (
    <div
      className={`cr-notice ${error ? "error" : ""}`}
      role={error ? "alert" : "status"}
    >
      {children}
    </div>
  );
}
function Empty({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="cr-empty">
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}
function Status({
  value,
  account = false,
}: {
  value: unknown;
  account?: boolean;
}) {
  return (
    <span className={`cr-status ${String(value)}`}>
      {account ? accountStatusLabel(value) : label(value)}
    </span>
  );
}
function Footer() {
  return (
    <footer className="cr-footer">
      <Brand compact />
      <p>
        Telephone service
        <br />
        for home &amp; business.
      </p>
      <nav aria-label="Footer">
        <a href="/privacy">Privacy</a>
        <a href="/website-use">Website use</a>
        <a href={portalLink("/admin")}>Staff sign in</a>
      </nav>
      <small>© {new Date().getFullYear()} Cache River Telephone Company</small>
    </footer>
  );
}

function InquiryForm() {
  const [inquiryKind, setInquiryKind] = useState("residential");
  useEffect(() => {
    const choose = (event: Event) => {
      const kind = (event as CustomEvent<unknown>).detail;
      if (
        kind === "residential" ||
        kind === "commercial" ||
        kind === "enterprise"
      )
        setInquiryKind(kind);
    };
    window.addEventListener("cr-inquiry-kind", choose);
    return () => window.removeEventListener("cr-inquiry-kind", choose);
  }, []);
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try {
      const result = await request<{ reference: string }>("/requests", {
        method: "POST",
        body: { ...values, requestKey },
      });
      setReference(result.reference);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (reference)
    return (
      <div className="cr-receipt" tabIndex={-1}>
        <span className="cr-kicker">Request received</span>
        <h3>We have your details.</h3>
        <p>
          Your reference is <strong>{reference}</strong>. We’ll use the contact
          details you supplied to follow up.
        </p>
        <p>
          This request has not activated service or changed your existing
          telephone number.
        </p>
        <button
          className="cr-button secondary"
          onClick={() => {
            setReference("");
            setRequestKey(crypto.randomUUID());
          }}
        >
          Send another request
        </button>
      </div>
    );
  return (
    <form className="cr-form" onSubmit={submit}>
      <div className="cr-form-grid">
        <Field title="Your name">
          <input name="name" autoComplete="name" required maxLength={120} />
        </Field>
        <Field title="Email address">
          <input
            name="email"
            type="email"
            autoComplete="email"
            required
            maxLength={254}
          />
        </Field>
        <Field title="Telephone number">
          <input name="phone" type="tel" autoComplete="tel" maxLength={40} />
        </Field>
        <Field title="What can we help with?">
          <select
            name="kind"
            value={inquiryKind}
            onChange={(event) => setInquiryKind(event.target.value)}
          >
            <option value="residential">Home telephone service</option>
            <option value="commercial">Commercial telephone service</option>
            <option value="enterprise">Enterprise · MozartCX</option>
            <option value="support">An existing account</option>
            <option value="privacy">A privacy request</option>
          </select>
        </Field>
      </div>
      <Field title="Service address">
        <input name="address" autoComplete="street-address" maxLength={500} />
      </Field>
      <Field title="Tell us what you need">
        <textarea
          name="message"
          rows={4}
          required
          minLength={10}
          maxLength={4000}
        />
      </Field>
      <div className="cr-honeypot" aria-hidden="true">
        <label>
          Website
          <input name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      <p className="cr-form-note">
        Please leave out passwords, payment details, and account PINs. We use
        this information to respond to your request.{" "}
        <a href="/privacy">Read our privacy notice.</a>
      </p>
      {error && <Notice error>{error}</Notice>}
      <button className="cr-button" disabled={busy}>
        {busy ? "Sending your request…" : "Send my request"}
      </button>
    </form>
  );
}

function Home() {
  const homeRoot = useRef<HTMLDivElement>(null);
  useLandingMotion(homeRoot);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    document.title =
      "Cache River Telephone | Residential, Commercial & MozartCX";
  }, []);
  function chooseService(kind: string) {
    window.dispatchEvent(new CustomEvent("cr-inquiry-kind", { detail: kind }));
  }
  return (
    <div className="cr-public cr-home" ref={homeRoot}>
      <a className="cr-skip" href="#main">
        Skip to content
      </a>
      <div className="cr-home-utility">
        <a href={portalLink("/customer")}>
          Already a customer? Sign in <span aria-hidden="true">↗</span>
        </a>
      </div>
      <header className="cr-home-header">
        <div className="cr-home-nav-inner">
          <Brand />
          <button
            ref={menuButton}
            className="cr-home-menu"
            aria-expanded={menuOpen}
            aria-controls="cr-home-navigation"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? "Close" : "Menu"}{" "}
            <span aria-hidden="true">{menuOpen ? "×" : "+"}</span>
          </button>
          <nav
            id="cr-home-navigation"
            className={menuOpen ? "is-open" : ""}
            aria-label="Main navigation"
            onClick={() => setMenuOpen(false)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setMenuOpen(false);
                menuButton.current?.focus();
              }
            }}
          >
            <a href="#residential">Residential</a>
            <a href="#commercial">Commercial</a>
            <a href="#enterprise">Enterprise</a>
            <a className="cr-button" href={portalLink("/register")}>
              Get connected <span aria-hidden="true">↗</span>
            </a>
          </nav>
        </div>
      </header>
      <main id="main">
        <section className="cr-home-hero" aria-labelledby="cr-home-title">
          <div className="cr-home-hero-inner">
            <div className="cr-home-hero-copy">
              <p className="cr-kicker">Cache River Telephone Company</p>
              <h1 id="cr-home-title">
                Good conversations
                <br />
                start <em>here.</em>
              </h1>
              <p className="cr-home-intro">
                From your home phone to the way your whole organization
                connects. Find your next connection with Cache River.
              </p>
              <div className="cr-home-hero-actions">
                <a className="cr-button" href="#service">
                  Find your service <span aria-hidden="true">↗</span>
                </a>
                <a className="cr-home-text-link" href="#contact">
                  Let’s talk <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
            <div className="cr-home-emblem">
              <img
                src="/brand/cache-river-emblem-v6.png?v=66559bc5"
                alt="Cache River’s cypress tree and winding river emblem"
                width="420"
                height="420"
                fetchPriority="high"
              />
              <div className="cr-home-wordmark">
                Cache River<span>TELEPHONE COMPANY</span>
              </div>
            </div>
          </div>
          <div className="cr-home-hero-bottom">
            <span>One company. Three ways to connect.</span>
            <a href="#service">
              Explore Cache River <span aria-hidden="true">↓</span>
            </a>
          </div>
        </section>
        <section
          id="service"
          className="cr-home-services"
          aria-labelledby="cr-services-title"
        >
          <div className="cr-home-section-heading" data-reveal>
            <div>
              <p className="cr-kicker">A connection that fits</p>
              <h2 id="cr-services-title">
                Your world.
                <br />
                Your way to connect.
              </h2>
            </div>
            <p>
              A familiar voice at home. The right person at work. A connected
              team across locations. Let’s build around what you need.
            </p>
          </div>
          <div className="cr-home-service-grid">
            <article
              id="residential"
              className="cr-home-service-card"
              data-reveal
            >
              <div className="cr-home-card-top">
                <span>01 / Residential</span>
                <span aria-hidden="true">↗</span>
              </div>
              <h3>
                Keep home
                <br />
                within reach.
              </h3>
              <p>
                Telephone service for the everyday conversations that matter.
                Ask about keeping your number and getting connected at your
                address.
              </p>
              <ul>
                <li>Home telephone service</li>
                <li>Number transfer options</li>
                <li>Help with your equipment</li>
              </ul>
              <a href="#contact" onClick={() => chooseService("residential")}>
                Let’s connect your home <span aria-hidden="true">→</span>
              </a>
            </article>
            <article
              id="commercial"
              className="cr-home-service-card"
              data-reveal
            >
              <div className="cr-home-card-top">
                <span>02 / Commercial</span>
                <span aria-hidden="true">↗</span>
              </div>
              <h3>
                Make every
                <br />
                call count.
              </h3>
              <p>
                Give customers a clear way to reach your business. Plan your
                phones and call flow around the people who keep it moving.
              </p>
              <ul>
                <li>Business phones and extensions</li>
                <li>Welcoming call menus</li>
                <li>Routing for your team</li>
              </ul>
              <a href="#contact" onClick={() => chooseService("commercial")}>
                Let’s connect your business <span aria-hidden="true">→</span>
              </a>
            </article>
            <article
              id="enterprise"
              className="cr-home-service-card cr-home-enterprise"
              data-reveal
            >
              <div className="cr-home-card-top">
                <span>03 / Enterprise</span>
                <span aria-hidden="true">↗</span>
              </div>
              <h3>
                Mozart<span>CX</span>
              </h3>
              <p className="cr-home-product-byline">
                A Cache River company. Built for enterprise.
              </p>
              <p>
                Bring your teams, locations, and customer conversations together
                with a proposal built around your organization.
              </p>
              <ul>
                <li>Teams across locations</li>
                <li>Calling and customer-service needs</li>
                <li>A custom service proposal</li>
              </ul>
              <a
                href="https://mozartcx.com"
                aria-label="Explore MozartCX on its website"
              >
                Explore MozartCX <span aria-hidden="true">↗</span>
              </a>
              <a
                className="cr-home-enterprise-inquiry"
                href="#contact"
                onClick={() => chooseService("enterprise")}
              >
                Ask Cache River about Enterprise
              </a>
            </article>
          </div>
        </section>
        <section className="cr-home-account" data-reveal>
          <div>
            <p className="cr-kicker">Already part of Cache River?</p>
            <h2>
              Your account.
              <br />
              Right at your fingertips.
            </h2>
          </div>
          <div>
            <p>
              Service records, billing documents, and a direct way to message
              our staff—all in your customer portal.
            </p>
            <a className="cr-button" href={portalLink("/customer")}>
              Open your account <span aria-hidden="true">↗</span>
            </a>
          </div>
        </section>
        <section
          id="contact"
          className="cr-contact cr-home-contact"
          data-reveal
        >
          <div>
            <p className="cr-kicker">Let’s get you connected</p>
            <h2>
              It starts with
              <br />a conversation.
            </h2>
            <p>
              Tell us a little about what you need. Our team will help you work
              through the options.
            </p>
            <p className="cr-fineprint">
              Availability, rates, equipment, and any number transfer must be
              confirmed before service begins.
            </p>
          </div>
          <InquiryForm />
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Policy({ privacy }: { privacy: boolean }) {
  return (
    <div className="cr-public">
      <header className="cr-header">
        <Brand />
        <a href="/">Return home</a>
      </header>
      <main className="cr-policy">
        <p className="cr-kicker">Cache River Telephone Company</p>
        <h1>{privacy ? "Privacy notice" : "Using this website"}</h1>
        {privacy ? (
          <>
            <p>
              We collect the contact information and message you submit when you
              ask about service or contact us. Customer and staff portals also
              store account access, service records, support messages, and
              security events.
            </p>
            <h2>How we use your information</h2>
            <p>
              We use it to respond to requests, manage account access, maintain
              service records, and protect the portal. Please do not submit
              payment card information, passwords, or telephone account PINs in
              a message.
            </p>
            <h2>Cookies and hosting</h2>
            <p>
              The portal uses an essential session cookie to keep you signed in.
              This site does not include advertising trackers. Our hosting and
              network providers process requests needed to deliver and protect
              the website.
            </p>
            <h2>Account information</h2>
            <p>
              Customers can view records associated with the accounts they have
              been authorized to access. Staff access is restricted to the
              administration portal. We retain messages and operational records
              to manage the request or account, subject to applicable
              requirements.
            </p>
            <h2>Contact us about privacy</h2>
            <p>
              Use the <a href="/#contact">contact form</a> and choose “A privacy
              request” to ask about access, correction, or deletion of your
              information. We may need to verify your identity before changing
              account records.
            </p>
          </>
        ) : (
          <>
            <p>
              This website lets you ask about telephone service, access an
              authorized customer account, and communicate with Cache River
              staff.
            </p>
            <h2>Service requests</h2>
            <p>
              Submitting an inquiry does not order, activate, cancel, or
              transfer telephone service. Availability and pricing require
              confirmation. Any service agreement and emergency calling
              information will be provided separately before activation.
            </p>
            <h2>Account access</h2>
            <p>
              Use only an account you are authorized to access. Keep your
              password and invitation links private. Sign out when using a
              shared device.
            </p>
            <h2>Support messages</h2>
            <p>
              Messages submitted here are not monitored as an emergency service.
              For an emergency, contact emergency services using an available
              telephone connection.
            </p>
          </>
        )}
        <a className="cr-button secondary" href="/">
          Return to Cache River
        </a>
      </main>
      <Footer />
    </div>
  );
}

function Login({
  admin,
  onSession,
}: {
  admin: boolean;
  onSession: (session: Session) => void;
}) {
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function passkeyLogin() {
    setBusy(true);
    setError("");
    try {
      const options = await request<PublicKeyCredentialRequestOptionsJSON>(
        "/passkeys/login/options",
        { method: "POST", body: {} },
      );
      const response = await startAuthentication({ optionsJSON: options });
      const next = await request<Session>("/passkeys/login/verify", {
        method: "POST",
        body: response,
      });
      onSession(next);
    } catch (cause) {
      setError(
        (cause as Error).name === "NotAllowedError"
          ? "Passkey sign-in was canceled or timed out. Try again, or use your password and authenticator."
          : (cause as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const session = await request<Session | { mfaRequired: true }>("/login", {
        method: "POST",
        body: Object.fromEntries(new FormData(event.currentTarget)),
      });
      if ("mfaRequired" in session) {
        setMfaRequired(true);
        return;
      }
      onSession(session);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="cr-auth">
      <header>
        <Brand />
      </header>
      <main>
        <div className="cr-auth-context">
          <p className="cr-kicker">
            {admin ? "Staff access" : "Customer access"}
          </p>
          <h1>
            {admin
              ? "Cache River\nadministration."
              : "Your Cache River\naccount."}
          </h1>
          <p>
            {admin
              ? "Manage customer records, service inquiries, and account messages."
              : "Your service details, account documents, and conversations with Cache River in one place."}
          </p>
          <a href="/">Return to the website</a>
        </div>
        <form className="cr-login-panel cr-form" onSubmit={submit}>
          <h2>{admin ? "Admin sign in" : "Customer sign in"}</h2>
          {admin && browserSupportsWebAuthn() && (
            <>
              <button
                type="button"
                className="cr-button"
                disabled={busy}
                onClick={() => void passkeyLogin()}
              >
                Sign in with a passkey
              </button>
              <p className="cr-form-note">
                Or sign in with your password and authenticator below.
              </p>
            </>
          )}
          <Field title="Email address">
            <input
              type="email"
              name="email"
              required
              autoComplete="username"
              maxLength={254}
            />
          </Field>
          <Field title="Password">
            <input
              type="password"
              name="password"
              required
              autoComplete="current-password"
              maxLength={128}
            />
          </Field>
          {mfaRequired && (
            <Field title="Authenticator or recovery code">
              <input
                name="code"
                autoComplete="one-time-code"
                required
                maxLength={50}
                autoFocus
              />
              <p className="cr-form-note">
                Enter the six-digit code from your authenticator, or one unused
                recovery code.
              </p>
            </Field>
          )}
          {error && <Notice error>{error}</Notice>}
          <button className="cr-button" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <p className="cr-form-note">
            {admin
              ? "Staff accounts are created by an administrator."
              : "First visit? Open the invitation link supplied by Cache River to set your password."}
          </p>
          {!admin && (
            <a href={portalLink("/register")}>
              New customer? Register for service.
            </a>
          )}
          <a href="/#contact">Need help signing in?</a>
        </form>
      </main>
    </div>
  );
}

function RecoverPassword() {
  const [token] = useState(
    () => new URLSearchParams(location.hash.slice(1)).get("token") ?? "",
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (hostedCopy)
      location.replace(`${portalLink("/recover")}${location.hash}`);
    else history.replaceState(null, "", location.pathname);
  }, []);
  return (
    <div className="cr-auth">
      <header>
        <Brand />
      </header>
      <main>
        <h1>Reset your password</h1>
        {hostedCopy ? (
          <p role="status">Opening secure account recovery…</p>
        ) : done ? (
          <>
            <p role="status">
              Your password has been changed and previous sessions have been
              signed out.
            </p>
            <a href="/customer">Sign in to your account</a>
          </>
        ) : !token ? (
          <>
            <p>
              This page needs the private recovery link issued by Cache River.
            </p>
            <a href="/#contact">Request help signing in</a>
          </>
        ) : (
          <form
            className="cr-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const values = new FormData(e.currentTarget);
              setError("");
              if (values.get("password") !== values.get("confirm")) {
                setError("The passwords do not match.");
                return;
              }
              setBusy(true);
              try {
                await request("/recover", {
                  method: "POST",
                  body: { token, password: values.get("password") },
                });
                sessionBoundary.changed();
                setDone(true);
              } catch (cause) {
                setError((cause as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <p>
              This one-use link expires 30 minutes after it was issued. Your new
              password applies to all accounts using this login.
            </p>
            <Field title="New password, at least 15 characters">
              <input
                name="password"
                type="password"
                required
                minLength={15}
                maxLength={128}
                autoComplete="new-password"
              />
            </Field>
            <Field title="Confirm new password">
              <input
                name="confirm"
                type="password"
                required
                minLength={15}
                maxLength={128}
                autoComplete="new-password"
              />
            </Field>
            {error && <Notice error>{error}</Notice>}
            <button className="cr-button" disabled={busy}>
              {busy ? "Changing password…" : "Change password"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}

function AcceptInvitation({
  onSession,
}: {
  onSession: (session: Session) => void;
}) {
  const [token] = useState(
    () => new URLSearchParams(location.hash.slice(1)).get("token") ?? "",
  );
  const [signingIn, setSigningIn] = useState(false);
  const [linkedSession, setLinkedSession] = useState<Session | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    history.replaceState(null, "", location.pathname);
  }, []);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const values = Object.fromEntries(new FormData(event.currentTarget));
    if (!linkedSession && values.password !== values.confirm) {
      setError("The passwords do not match.");
      setBusy(false);
      return;
    }
    try {
      const result = await request<Session>("/accept-invitation", {
        method: "POST",
        body: {
          token,
          ...(!linkedSession
            ? { displayName: values.displayName, password: values.password }
            : {}),
        },
      });
      history.replaceState(
        null,
        "",
        result.user?.kind === "admin" ? "/admin" : "/customer",
      );
      onSession(result);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (signingIn)
    return (
      <Login
        admin={false}
        onSession={(session) => {
          setLinkedSession(session);
          setSigningIn(false);
          setError("");
        }}
      />
    );
  return (
    <div className="cr-auth">
      <header>
        <Brand />
      </header>
      <main>
        <div className="cr-auth-context">
          <p className="cr-kicker">Account invitation</p>
          <h1>
            Welcome to
            <br />
            Cache River.
          </h1>
          <p>
            {linkedSession
              ? "Link this invitation to your existing sign-in."
              : "Choose the name and password you’ll use to access your account."}
          </p>
        </div>
        {token ? (
          <form className="cr-login-panel cr-form" onSubmit={submit}>
            <h2>
              {linkedSession ? "Link your account" : "Set up your access"}
            </h2>
            {linkedSession ? (
              <p>
                Signed in as {linkedSession.user?.email}. Your existing password
                will stay the same.
              </p>
            ) : (
              <>
                <Field title="Your name">
                  <input
                    name="displayName"
                    required
                    autoComplete="name"
                    maxLength={120}
                  />
                </Field>
                <Field title="New password">
                  <input
                    name="password"
                    type="password"
                    minLength={15}
                    maxLength={128}
                    required
                    autoComplete="new-password"
                  />
                </Field>
                <Field title="Confirm password">
                  <input
                    name="confirm"
                    type="password"
                    minLength={15}
                    maxLength={128}
                    required
                    autoComplete="new-password"
                  />
                </Field>
                <p className="cr-form-note">
                  Use at least 15 characters. A phrase of several unrelated
                  words works well.
                </p>
              </>
            )}
            {error && <Notice error>{error}</Notice>}
            <button className="cr-button" disabled={busy}>
              {busy
                ? "Setting up access…"
                : linkedSession
                  ? "Link this account"
                  : "Create my password"}
            </button>
            <button
              className="cr-text-button"
              type="button"
              onClick={() => setSigningIn(true)}
            >
              {linkedSession
                ? "Sign in with a different email"
                : "Already have access? Sign in to link this account"}
            </button>
          </form>
        ) : (
          <Empty title="This link has no invitation">
            Open the complete link supplied by Cache River, or{" "}
            <a href="/#contact">request help</a>.
          </Empty>
        )}
      </main>
    </div>
  );
}

function Registration({
  onSession,
}: {
  onSession: (session: Session) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [kind, setKind] = useState("residential");
  const [numberMode, setNumberMode] = useState("new");
  const requestKey = useRef(crypto.randomUUID());
  useEffect(() => {
    document.title = "Register for service | Cache River";
  }, []);
  return (
    <div className="cr-auth cr-registration">
      <header>
        <Brand />
      </header>
      <main>
        <section className="cr-auth-context">
          <p className="cr-kicker">New customer</p>
          <h1>Register for service</h1>
          <p>
            Create your sign-in and tell us where you need service. Staff will
            review your application before any number, equipment, or service
            changes.
          </p>
          <p>
            Already have an account?{" "}
            <a href={portalLink("/customer")}>Sign in.</a>
          </p>
        </section>
        <form
          className="cr-form"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const value = (key: string) => String(form.get(key) ?? "");
            if (value("password") !== value("confirmPassword")) {
              setError("The passwords do not match.");
              return;
            }
            setBusy(true);
            setError("");
            try {
              const next = await request<Session>("/register", {
                method: "POST",
                body: {
                  requestKey: requestKey.current,
                  kind,
                  name:
                    kind === "residential"
                      ? value("contactName")
                      : value("name"),
                  contactName: value("contactName"),
                  billingEmail: value("billingEmail"),
                  password: value("password"),
                  address: {
                    line1: value("line1"),
                    line2: value("line2"),
                    city: value("city"),
                    region: value("region"),
                    postalCode: value("postalCode"),
                  },
                  numberRequest:
                    numberMode === "existing"
                      ? {
                          mode: "existing",
                          number: value("number"),
                          carrier: value("carrier"),
                        }
                      : numberMode === "new"
                        ? {
                            mode: "new",
                            ...(value("areaCode")
                              ? { areaCode: value("areaCode") }
                              : {}),
                          }
                        : { mode: "later" },
                  reviewAcknowledged: form.get("reviewAcknowledged") === "on",
                },
              });
              history.replaceState(null, "", "/customer");
              onSession(next);
            } catch (cause) {
              setError((cause as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <fieldset disabled={busy}>
            <legend>Your account</legend>
            <Field title="Service type">
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="residential">Home</option>
                <option value="commercial">Commercial</option>
                <option value="enterprise">Enterprise · MozartCX</option>
              </select>
            </Field>
            {kind !== "residential" && (
              <Field title="Business or organization name">
                <input
                  name="name"
                  required
                  maxLength={200}
                  autoComplete="organization"
                />
              </Field>
            )}
            <Field title="Your full name">
              <input
                name="contactName"
                required
                maxLength={120}
                autoComplete="name"
              />
            </Field>
            <Field title="Email for sign-in">
              <input
                name="billingEmail"
                type="email"
                required
                maxLength={254}
                autoComplete="email"
              />
            </Field>
            <Field title="Password (at least 15 characters)">
              <input
                name="password"
                type="password"
                required
                minLength={15}
                maxLength={128}
                autoComplete="new-password"
              />
            </Field>
            <Field title="Repeat password">
              <input
                name="confirmPassword"
                type="password"
                required
                minLength={15}
                maxLength={128}
                autoComplete="new-password"
              />
            </Field>
          </fieldset>
          <fieldset disabled={busy}>
            <legend>Service address</legend>
            <Field title="Street address">
              <input
                name="line1"
                required
                maxLength={200}
                autoComplete="address-line1"
              />
            </Field>
            <Field title="Apartment, suite or unit (optional)">
              <input
                name="line2"
                maxLength={120}
                autoComplete="address-line2"
              />
            </Field>
            <Field title="City">
              <input
                name="city"
                required
                maxLength={100}
                autoComplete="address-level2"
              />
            </Field>
            <div className="cr-form-grid">
              <Field title="State abbreviation">
                <input
                  name="region"
                  required
                  pattern="[A-Za-z]{2}"
                  maxLength={2}
                  defaultValue="IL"
                  autoComplete="address-level1"
                />
              </Field>
              <Field title="ZIP code">
                <input
                  name="postalCode"
                  required
                  pattern="[0-9]{5}(-[0-9]{4})?"
                  inputMode="numeric"
                  autoComplete="postal-code"
                />
              </Field>
            </div>
            <p className="cr-form-note">
              Saving this address does not verify it for emergency calling. That
              is a separate required setup step.
            </p>
          </fieldset>
          <fieldset disabled={busy}>
            <legend>Your telephone number</legend>
            <Field title="What would you like to do?">
              <select
                value={numberMode}
                onChange={(e) => setNumberMode(e.target.value)}
              >
                <option value="new">Choose a new number</option>
                <option value="existing">Keep my existing number</option>
                <option value="later">Decide with staff</option>
              </select>
            </Field>
            {numberMode === "new" && (
              <>
                <Field title="Different area code (optional)">
                  <input
                    name="areaCode"
                    pattern="[2-9][0-9]{2}"
                    maxLength={3}
                    inputMode="numeric"
                  />
                </Field>
                <p className="cr-form-note">
                  Leave blank for numbers near your service address. Staff will
                  confirm available choices after review; no number is reserved
                  here.
                </p>
              </>
            )}
            {numberMode === "existing" && (
              <>
                <Field title="Number to keep">
                  <input
                    name="number"
                    type="tel"
                    required
                    maxLength={30}
                    autoComplete="tel"
                  />
                </Field>
                <Field title="Current telephone provider">
                  <input name="carrier" required maxLength={120} />
                </Field>
                <p className="cr-form-note">
                  Do not cancel your current service. This records a request,
                  not a number transfer.
                </p>
              </>
            )}
          </fieldset>
          <label className="cr-check">
            <input type="checkbox" name="reviewAcknowledged" required />{" "}
            <span>
              I understand that registration does not activate phone service or
              authorize charges. Staff will confirm availability, a plan and
              setup with me.
            </span>
          </label>
          <p className="cr-form-note">
            Your email is not verified automatically.{" "}
            <a href="/privacy">Privacy notice</a>
          </p>
          {error && <Notice error>{error}</Notice>}
          <button className="cr-button" disabled={busy}>
            {busy ? "Creating your account…" : "Create my account"}
          </button>
          <a href={portalLink("/customer")}>Already registered? Sign in.</a>
        </form>
      </main>
    </div>
  );
}

function Table({
  rows,
  columns,
  empty,
}: {
  rows: Row[];
  columns: [string, string, ((row: Row) => ReactNode)?][];
  empty: string;
}) {
  return rows.length ? (
    <div className="cr-table-wrap" tabIndex={0} aria-label="Scrollable records">
      <table>
        <thead>
          <tr>
            {columns.map(([key, title]) => (
              <th key={key}>{title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id ?? i}>
              {columns.map(([key, , render]) => (
                <td key={key}>
                  {render ? render(row) : String(row[key] ?? "Not recorded")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty title={empty}>
      Records will appear here when they are added to your account.
    </Empty>
  );
}

function CustomerViewForm({
  onStart,
}: {
  onStart: (reason: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <form
      className="cr-form"
      onSubmit={async (event) => {
        event.preventDefault();
        const reason = String(
          new FormData(event.currentTarget).get("reason") ?? "",
        );
        setBusy(true);
        setError("");
        try {
          await onStart(reason);
        } catch (cause) {
          setError((cause as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="cr-field">
        <span>Reason for opening customer view</span>
        <input name="reason" required minLength={5} maxLength={300} />
      </label>
      <button className="cr-button" disabled={busy}>
        {busy ? "Opening…" : "Open customer view"}
      </button>
      {error && <Notice error>{error}</Notice>}
    </form>
  );
}

export function AccountDetail({
  workspace,
  customer = false,
  csrf,
  onSaved,
  refreshVersion,
  readOnly = false,
  onCustomerView,
  onHelp,
  onBack,
  tab,
  onTab,
}: {
  workspace: Workspace;
  customer?: boolean;
  csrf: string | undefined;
  onSaved: () => Promise<void>;
  refreshVersion: number;
  readOnly?: boolean;
  onCustomerView?: (reason: string) => Promise<void>;
  onHelp?: (() => void) | undefined;
  onBack?: (() => void) | undefined;
  tab: AccountTab;
  onTab: (tab: AccountTab) => void;
}) {
  const commercial = workspace.account.kind !== "residential";
  const awaitingReview = Boolean(
    workspace.registration && !workspace.registration.reviewed_at,
  );
  readOnly = readOnly || (customer && awaitingReview);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [workflowCheck, setWorkflowCheck] = useState<{
    configurationHash: string;
    readyForCallingConfirmation: boolean;
  } | null>(null);
  const additionalTabs: AccountTab[] =
    customer && !awaitingReview
      ? [
          "services",
          "routing",
          "orders",
          ...(!commercial ? ["users" as const] : []),
        ]
      : [];
  const tabs =
    customer && awaitingReview
      ? (["overview", "orders"] as const)
      : accountTabs.filter((item) => !additionalTabs.includes(item));
  const w = workspace;
  const connectionState = useAccountConnections(
    request,
    String(w.account.id),
    customer,
    refreshVersion,
  );
  const [assignmentFocus, setAssignmentFocus] = useState<ConnectionFocus>({});
  const editAssignment = (focus: ConnectionFocus) => {
    setAssignmentFocus(focus);
    onTab("routing");
  };
  const tools = {
    api: request,
    csrf,
    accountId: String(w.account.id),
    customer,
    onSaved,
    readOnly,
  };
  return (
    <>
      <div className="cr-account-heading">
        <div>
          {onBack && (
            <button className="cr-text-button cr-account-back" onClick={onBack}>
              {customer ? "All accounts" : "Back to customers"}
            </button>
          )}
          <p className="cr-kicker">{w.account.account_number}</p>
          <h2>{w.account.name}</h2>
        </div>
        <Status value={w.account.status} account />
      </div>
      {!customer &&
        !readOnly &&
        ["draft", "pending_activation"].includes(w.account.status) && (
          <section className="cr-panel">
            <h3>Finish account setup</h3>
            <SetupChecklist
              api={request}
              accountId={String(w.account.id)}
              refreshVersion={refreshVersion}
              onTab={onTab}
            />
            <button
              className="cr-button"
              disabled={reviewBusy}
              onClick={async () => {
                setReviewBusy(true);
                setReviewError("");
                setWorkflowCheck(null);
                try {
                  const result = await request<{
                    configurationHash: string;
                    readyForCallingConfirmation: boolean;
                  }>(`/admin/accounts/${w.account.id}/onboarding/recheck`, {
                    method: "POST",
                    csrf,
                    body: {},
                  });
                  await onSaved();
                  setWorkflowCheck(result);
                  onTab("orders");
                } catch (cause) {
                  setReviewError((cause as Error).message);
                } finally {
                  setReviewBusy(false);
                }
              }}
            >
              {reviewBusy
                ? "Checking setup…"
                : "Check setup and open next steps"}
            </button>
            {reviewError && <Notice error>{reviewError}</Notice>}
          </section>
        )}
      {w.account.kind === "enterprise" && (
        <section className="cr-panel">
          <h3>MozartCX Enterprise</h3>
          <p>
            Enterprise communications managed by Cache River Telephone. Your
            service proposal confirms the teams, locations and features
            included.
          </p>
          {!customer && (
            <a
              className="cr-text-button"
              href="https://operator.mozartcx.com"
              target="_blank"
              rel="noreferrer"
            >
              Open MozartCX administration
            </a>
          )}
        </section>
      )}
      {awaitingReview && (
        <section className="cr-panel">
          <h3>Registration awaiting review</h3>
          <p>
            {customer
              ? "Your account is saved. Staff will confirm your application, available numbers and service plan. You can send questions from Messages."
              : "Confirm this customer through an independent contact before enabling service setup. The submitted email is unverified."}
          </p>
          {!customer && !readOnly && (
            <form
              className="cr-form"
              onSubmit={async (event) => {
                event.preventDefault();
                const form = event.currentTarget;
                setReviewBusy(true);
                setReviewError("");
                try {
                  await request(
                    `/admin/accounts/${w.account.id}/registration-review`,
                    {
                      method: "POST",
                      csrf,
                      body: {
                        reason: String(new FormData(form).get("reason") ?? ""),
                        customerConfirmed: true,
                      },
                    },
                  );
                  await onSaved();
                } catch (cause) {
                  setReviewError((cause as Error).message);
                } finally {
                  setReviewBusy(false);
                }
              }}
            >
              <Field title="How did you confirm this customer?">
                <textarea
                  name="reason"
                  required
                  minLength={20}
                  maxLength={1000}
                  rows={3}
                />
              </Field>
              <label className="cr-check">
                <input type="checkbox" required />{" "}
                <span>
                  I independently confirmed the customer and authorize account
                  setup. This does not verify E911 or activate service.
                </span>
              </label>
              {reviewError && <Notice error>{reviewError}</Notice>}
              <button className="cr-button" disabled={reviewBusy}>
                {reviewBusy
                  ? "Saving review…"
                  : "Approve registration for setup"}
              </button>
            </form>
          )}
        </section>
      )}
      {!customer && (!readOnly || onCustomerView) && (
        <div className="cr-account-tools" aria-label="Staff account actions">
          {!readOnly && (
            <AccountStatusPanel
              key={`account-status:${w.account.id}`}
              account={w.account as any}
              api={request}
              csrf={csrf}
              onSaved={onSaved}
            />
          )}
          {onCustomerView && (
            <details className="cr-panel cr-editor">
              <summary>View as customer</summary>
              <p>
                Open this account's customer portal for 15 minutes. Read-only
                access, recorded under your staff identity. Exit to make changes
                as staff.
              </p>
              <CustomerViewForm onStart={onCustomerView} />
            </details>
          )}
        </div>
      )}
      <AccountNavigation
        tabs={tabs}
        selected={tab}
        commercial={commercial}
        onTab={onTab}
        additionalTabs={additionalTabs}
      />
      {connectionState.error && tab !== "overview" && (
        <div className="cr-notice error" role="alert">
          Could not load number-to-phone connections. {connectionState.error}{" "}
          <button
            className="cr-button secondary"
            onClick={connectionState.reload}
          >
            Refresh connections
          </button>
        </div>
      )}
      {tab === "recordings" && (
        <VoiceStudio
          key={`recordings:${w.account.id}`}
          csrf={csrf}
          accountId={String(w.account.id)}
          customer={customer}
          readOnly={readOnly}
        />
      )}
      {tab === "overview" && (
        <div className="cr-detail-grid">
          {customer && !awaitingReview && (
            <CustomerAccountHome
              accountId={String(w.account.id)}
              account={{
                name: String(w.account.name),
                kind: String(w.account.kind),
              }}
              numbers={w.numbers}
              devices={w.devices}
              connections={connectionState}
              api={request}
              refreshVersion={refreshVersion}
              onTab={onTab}
              onHelp={onHelp}
              readOnly={readOnly}
            />
          )}
          {customer && awaitingReview ? (
            <AccountServiceSummary data={w} customer readOnly onTab={onTab} />
          ) : (
            <AccountConnectionOverview
              state={connectionState}
              commercial={commercial}
              customer={customer}
              readOnly={readOnly}
              onTab={onTab}
              {...(!customer && !readOnly ? { onEdit: editAssignment } : {})}
            />
          )}
          <AccountPlanPanel
            key={`overview-plan:${w.account.id}`}
            api={request}
            csrf={csrf}
            accountId={String(w.account.id)}
            customer={customer}
            readOnly={readOnly}
            refreshVersion={refreshVersion}
          />
          <section className="cr-panel">
            <h3>Account details</h3>
            {w.setup && (
              <div className="cr-notice">
                <strong>Telephone setup request</strong>
                <p>{setupNumberMessage(w.setup.number_request, w.numbers)}</p>
              </div>
            )}
            <dl className="cr-details">
              <div>
                <dt>Contact</dt>
                <dd>{w.account.contact_name || "Not supplied"}</dd>
              </div>
              <div>
                <dt>Billing email</dt>
                <dd>{w.account.billing_email || "Not supplied"}</dd>
              </div>
              <div>
                <dt>Account type</dt>
                <dd>{label(w.account.kind)}</dd>
              </div>
              {w.account.billing_mode === "complimentary" && (
                <div>
                  <dt>Service charges</dt>
                  <dd>Complimentary · $0 per month</dd>
                </div>
              )}
              <div>
                <dt>Opened</dt>
                <dd>{date(w.account.created_at)}</dd>
              </div>
            </dl>
          </section>
          <section className="cr-panel">
            <h3>Service locations</h3>
            {w.locations.length ? (
              w.locations.map((row) => (
                <div key={row.id}>
                  <strong>{row.name}</strong>
                  <p>
                    {[
                      row.address?.line1,
                      row.address?.line2,
                      row.address?.city,
                      row.address?.region,
                      row.address?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <LocationCorrectionForm {...tools} location={row} />
                  <EmergencyCheckPanel
                    key={`emergency:${row.id}:${row.version}`}
                    {...tools}
                    location={row}
                  />
                </div>
              ))
            ) : (
              <Empty title="No service location yet">
                A location will appear after it has been confirmed for this
                account.
              </Empty>
            )}
          </section>
          {!customer && <LocationForm {...tools} />}
          {!customer && (
            <section className="cr-panel cr-wide">
              <h3>Account history</h3>
              <Table
                rows={w.audit}
                columns={[
                  ["summary", "Change"],
                  ["actor_name", "By"],
                  ["occurred_at", "Date", (r) => date(r.occurred_at)],
                ]}
                empty="No account changes recorded"
              />
            </section>
          )}
        </div>
      )}
      {tab === "services" && (
        <>
          <AccountPlanPanel
            key={`services-plan:${w.account.id}`}
            api={request}
            csrf={csrf}
            accountId={String(w.account.id)}
            customer={customer}
            readOnly={readOnly}
            refreshVersion={refreshVersion}
          />
          <ServiceConnectionsPanel
            {...tools}
            key={`services:${w.account.id}:${customer}:${readOnly}`}
            commercial={commercial}
            view="services"
            refreshVersion={refreshVersion}
            onRouting={() => onTab("routing")}
            onEquipment={() => onTab("devices")}
            onNumbers={() => onTab("numbers")}
            onExtensions={() => onTab("users")}
            onLocations={() => onTab("overview")}
          />
          {w.subscriptions.length > 0 && (
            <>
              <h3>Billing subscriptions</h3>
              <Table
                rows={w.subscriptions}
                columns={[
                  ["plan_name", "Service"],
                  ["quantity", "Quantity"],
                  ["status", "Status", (r) => <Status value={r.status} />],
                  [
                    "recurring_minor",
                    "Recurring charge",
                    (r) => money(r.recurring_minor),
                  ],
                ]}
                empty="No subscriptions recorded"
              />
            </>
          )}
        </>
      )}
      {tab === "users" && commercial && (
        <ExtensionsPanel
          onFeatures={() => onTab("features")}
          onEquipment={() => onTab("devices")}
          connectionState={connectionState}
          onAssignment={editAssignment}
          onChanged={onSaved}
          key={`extensions:${w.account.id}`}
          api={request}
          accountId={String(w.account.id)}
          customer={customer}
          csrf={csrf}
          readOnly={readOnly}
          locations={w.locations.map((row) => ({
            id: String(row.id),
            name: String(row.name),
          }))}
          devices={w.devices.map((row) => ({
            id: String(row.id),
            model: String(row.model),
            label: String(row.label ?? ""),
            location_id: row.location_id ?? null,
          }))}
          refreshVersion={refreshVersion}
          onRecordings={() => onTab("recordings")}
          onLocations={() => onTab("overview")}
        />
      )}
      {tab === "users" && !commercial && (
        <>
          {w.users.length > 0 && (
            <Table
              rows={w.users}
              columns={[
                ["display_name", "Name"],
                ["role_label", "Role"],
              ]}
              empty="No users recorded"
            />
          )}
          <ServiceConnectionsPanel
            {...tools}
            commercial={false}
            view="services"
            refreshVersion={refreshVersion}
            onRouting={() => onTab("routing")}
            onEquipment={() => onTab("devices")}
            onNumbers={() => onTab("numbers")}
            onExtensions={() => onTab("users")}
            onLocations={() => onTab("overview")}
          />
        </>
      )}
      {tab === "numbers" && (
        <>
          <button
            className="cr-button secondary"
            onClick={() => onTab("devices")}
          >
            {customer || readOnly
              ? "View equipment connections"
              : "Assign numbers to equipment"}
          </button>
          <NumbersPanel
            {...tools}
            accountName={String(w.account.name)}
            preferredNumber={
              w.setup?.number_request.mode === "new"
                ? w.setup.number_request.number
                : undefined
            }
            locations={w.locations.map((row) => ({
              id: String(row.id),
              name: String(row.name),
              address: row.address,
              version: row.version,
            }))}
            onSetup={() => onTab("routing")}
            connectionState={connectionState}
            onAssignment={editAssignment}
            numbers={w.numbers}
            refreshVersion={refreshVersion}
          />
        </>
      )}
      {tab === "experiences" && (
        <AccountExperiences
          api={request}
          csrf={csrf}
          accountId={String(w.account.id)}
          customer={customer}
          readOnly={readOnly}
        />
      )}
      {tab === "features" && (
        <>
          <IVRPanel
            key={`ivr:${w.account.id}:${customer}:${readOnly}`}
            api={request}
            csrf={csrf}
            accountId={String(w.account.id)}
            accountName={String(w.account.name)}
            customer={customer}
            readOnly={readOnly}
          />
          <FeaturesPanel
            key={`features:${w.account.id}:${customer}:${readOnly}`}
            api={request}
            csrf={csrf}
            accountId={String(w.account.id)}
            customer={customer}
            readOnly={readOnly}
          />
        </>
      )}
      {tab === "voicemail" && (
        <>
          <VoicemailPanel
            key={`voicemail:${w.account.id}:${customer}:${readOnly}`}
            api={request}
            refreshVersion={refreshVersion}
            csrf={csrf}
            accountId={String(w.account.id)}
            customer={customer}
            readOnly={readOnly}
          />
          <AccountMessagingPanel {...tools} />
        </>
      )}
      {tab === "devices" && (
        <EquipmentPanel
          {...tools}
          connectionState={connectionState}
          onAssignment={editAssignment}
          devices={w.devices}
          locations={w.locations}
          connections={
            <ServiceConnectionsPanel
              {...tools}
              key={`equipment-connections:${w.account.id}:${customer}:${readOnly}`}
              commercial={commercial}
              view="equipment"
              refreshVersion={refreshVersion}
              onRouting={() => onTab("routing")}
              onEquipment={() => onTab("devices")}
              onNumbers={() => onTab("numbers")}
              onExtensions={() => onTab("users")}
              onLocations={() => onTab("overview")}
            />
          }
        />
      )}
      {tab === "routing" && (
        <>
          <ServiceConnectionsPanel
            {...tools}
            key={`routing:${w.account.id}:${customer}:${readOnly}`}
            commercial={commercial}
            view="routing"
            focus={assignmentFocus}
            refreshVersion={refreshVersion}
            onRouting={() => onTab("routing")}
            onEquipment={() => onTab("devices")}
            onNumbers={() => onTab("numbers")}
            onExtensions={() => onTab("users")}
            onLocations={() => onTab("overview")}
          />
          {w.routes.length > 0 && (
            <>
              <h3>Earlier route records</h3>
              <p>
                These stored versions are not confirmation of the currently
                loaded phone-system configuration.
              </p>
              <Table
                rows={w.routes}
                columns={[
                  ["label", "Call route"],
                  [
                    "current_version",
                    "Recorded version",
                    (r) => r.current_version || "No version",
                  ],
                ]}
                empty="No call routes recorded"
              />
            </>
          )}
        </>
      )}
      {tab === "billing" && (
        <>
          <AccountBillingSummary data={w} />
          {!customer && !readOnly && (
            <AccountBillingArrangement
              key={`${w.account.id}:${w.account.version}`}
              account={w.account as any}
              api={request}
              csrf={csrf}
              onSaved={onSaved}
            />
          )}
          {w.invoices.length > 0 ? (
            <Table
              rows={w.invoices}
              columns={[
                ["invoice_number", "Invoice"],
                ["status", "Status", (r) => <Status value={r.status} />],
                ["total_minor", "Total", (r) => money(r.total_minor)],
                ["created_at", "Created", (r) => date(r.created_at)],
              ]}
              empty="No invoices issued"
            />
          ) : (
            <p>No invoices have been issued for this account.</p>
          )}
        </>
      )}
      {tab === "orders" && (
        <>
          {!customer && !readOnly && (
            <section className="cr-panel">
              <h3>Check setup progress</h3>
              {!["draft", "pending_activation"].includes(w.account.status) && (
                <SetupChecklist
                  api={request}
                  accountId={String(w.account.id)}
                  refreshVersion={refreshVersion}
                  onTab={onTab}
                />
              )}
              <p>
                Recheck saved plans, owned numbers, equipment connections and
                recent emergency-address evidence. This does not place a call or
                change live service.
              </p>
              <button
                className="cr-button"
                disabled={reviewBusy}
                onClick={async () => {
                  setReviewBusy(true);
                  setReviewError("");
                  setWorkflowCheck(null);
                  try {
                    const result = await request<{
                      configurationHash: string;
                      readyForCallingConfirmation: boolean;
                    }>(`/admin/accounts/${w.account.id}/onboarding/recheck`, {
                      method: "POST",
                      csrf,
                      body: {},
                    });
                    await onSaved();
                    setWorkflowCheck(result);
                  } catch (cause) {
                    setReviewError((cause as Error).message);
                  } finally {
                    setReviewBusy(false);
                  }
                }}
              >
                {reviewBusy ? "Checking setup…" : "Recheck setup"}
              </button>
              {workflowCheck?.readyForCallingConfirmation && (
                <form
                  className="cr-form"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    const reason = String(
                      new FormData(event.currentTarget).get("reason") ?? "",
                    );
                    setReviewBusy(true);
                    setReviewError("");
                    try {
                      await request(
                        `/admin/accounts/${w.account.id}/onboarding/confirm-calling`,
                        {
                          method: "POST",
                          csrf,
                          body: {
                            configurationHash: workflowCheck.configurationHash,
                            reason,
                            confirmedInboundOutboundAudioProvisioningAndEmergencyRouting: true,
                          },
                        },
                      );
                      setWorkflowCheck(null);
                      await onSaved();
                    } catch (cause) {
                      setReviewError((cause as Error).message);
                    } finally {
                      setReviewBusy(false);
                    }
                  }}
                >
                  <h4>Record an actual calling test</h4>
                  <p>
                    Do not dial emergency numbers as a routine test. Verify
                    emergency routing through the approved provider procedure. A
                    staff confirmation is not an automated test result.
                  </p>
                  <Field title="Test date, equipment and results">
                    <textarea
                      name="reason"
                      required
                      minLength={20}
                      maxLength={1000}
                      rows={3}
                    />
                  </Field>
                  <label className="cr-check">
                    <input type="checkbox" required />{" "}
                    <span>
                      I verified inbound and outbound calling, two-way audio,
                      provisioning and emergency routing for this configuration.
                    </span>
                  </label>
                  <button className="cr-button" disabled={reviewBusy}>
                    Record verified setup
                  </button>
                </form>
              )}
              {reviewError && <Notice error>{reviewError}</Notice>}
            </section>
          )}
          <Table
            rows={w.orders}
            columns={[
              ["order_number", "Order"],
              ["type", "Work", (r) => label(r.type)],
              ["status", "Status", (r) => <Status value={r.status} />],
              [
                "current_step",
                "Next action",
                (r) => label(r.current_step || "Review account"),
              ],
              ["updated_at", "Updated", (r) => date(r.updated_at)],
            ]}
            empty="No service orders recorded"
          />
          {!!w.onboardingSteps?.length && (
            <section className="cr-panel">
              <h3>Customer setup</h3>
              <p>
                These are the required setup steps. Only confirmed work is
                marked complete; this checklist does not activate phone service.
              </p>
              <ol>
                {w.onboardingSteps.map((step) => (
                  <li key={step.step_key}>
                    <strong>
                      {step.safe_result?.title ?? label(step.step_key)}
                    </strong>{" "}
                    <Status value={step.status} />
                    {!customer && (
                      <button
                        className="cr-text-button"
                        onClick={() =>
                          onTab(step.safe_result?.tab ?? "overview")
                        }
                      >
                        Open setup
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </>
      )}
    </>
  );
}

function VoiceStudio({
  csrf,
  accounts = [],
  accountId,
  customer = false,
  readOnly = false,
}: {
  csrf: string | undefined;
  accounts?: Row[];
  accountId?: string;
  customer?: boolean;
  readOnly?: boolean;
}) {
  const base = accountId
    ? "/" +
      (customer ? "customer" : "admin") +
      "/accounts/" +
      accountId +
      "/recordings"
    : "/admin/voice-studio";
  const [studio, setStudio] = useState<{
    available: boolean;
    voices: { id: string; name: string; description: string }[];
    prompts: Row[];
    jobs: Row[];
  } | null>(null);
  const [script, setScript] = useState("");
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState<Row | null>(null);
  const [attempt, setAttempt] = useState<Row | null>(null);
  const [lastJob, setLastJob] = useState<Row | null>(null);
  const lifetime = useRef<AbortController | null>(null);
  const polling = useRef(false);
  const load = useCallback(
    async (signal?: AbortSignal) => {
      const saved = await request<NonNullable<typeof studio>>(
        base,
        signal ? { signal } : {},
      );
      if (!signal?.aborted) setStudio(saved);
      return saved;
    },
    [base],
  );
  const monitor = useCallback(
    async (first: Row, signal: AbortSignal) => {
      if (polling.current) return;
      polling.current = true;
      setBusy(true);
      let job = first;
      try {
        while (job.status === "processing" && !signal.aborted) {
          setLastJob(job);
          await new Promise<void>((resolve) => {
            const done = () => {
              clearTimeout(timer);
              signal.removeEventListener("abort", done);
              resolve();
            };
            const timer = setTimeout(done, 4000);
            signal.addEventListener("abort", done, { once: true });
          });
          if (signal.aborted) return;
          try {
            job = await request<Row>(base + "/jobs/" + job.id, { signal });
            setError("");
          } catch (cause) {
            if (signal.aborted) return;
            const status = (cause as { status?: number }).status;
            if (status && status < 500) throw cause;
            setError(
              "Connection interrupted. Checking the same recording again; no new recording has been submitted.",
            );
          }
        }
        if (signal.aborted) return;
        setLastJob(job);
        setAttempt(null);
        if (job.status === "completed") setCurrent(job.result);
        else
          setError(
            job.detail ||
              "This recording was not saved. You can submit a new attempt.",
          );
        await load(signal);
      } catch (cause) {
        if (!signal.aborted) setError((cause as Error).message);
      } finally {
        polling.current = false;
        if (!signal.aborted) setBusy(false);
      }
    },
    [base, load],
  );
  const loadAndResume = useCallback(
    async (signal: AbortSignal) => {
      setBusy(true);
      try {
        const saved = await load(signal);
        if (signal.aborted) return;
        setError("");
        const active = saved.jobs.find((job) => job.status === "processing");
        if (active) await monitor(active, signal);
      } catch (cause) {
        if (!signal.aborted) setError((cause as Error).message);
      } finally {
        if (!signal.aborted && !polling.current) setBusy(false);
      }
    },
    [load, monitor],
  );
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    void loadAndResume(controller.signal);
    return () => controller.abort();
  }, [loadAndResume]);
  async function generate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const controller = lifetime.current;
    if (!controller || controller.signal.aborted || busy || readOnly) return;
    const body = attempt ?? {
      ...Object.fromEntries(new FormData(event.currentTarget)),
      requestKey: crypto.randomUUID(),
    };
    setAttempt(body);
    setError("");
    setLastJob(null);
    setBusy(true);
    try {
      const job = await request<Row>(base, {
        method: "POST",
        csrf,
        body,
        signal: controller.signal,
      });
      if (!controller.signal.aborted) await monitor(job, controller.signal);
    } catch (cause) {
      if (!controller.signal.aborted) {
        const status = (cause as { status?: number }).status;
        if (status && [400, 403, 404, 429].includes(status)) setAttempt(null);
        setError((cause as Error).message);
      }
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }
  return (
    <>
      <p className="cr-page-intro">
        Write a voicemail greeting, phone menu, or announcement. Choose a voice,
        listen, and download the recording. Files stay here when you leave the
        page.
      </p>
      {readOnly && (
        <Notice>
          Customer view is read-only. You can listen to saved recordings, but
          cannot create one.
        </Notice>
      )}
      {error && <Notice error>{error}</Notice>}
      {!studio ? (
        <p role="status">
          {error ? (
            <button
              className="cr-button secondary"
              disabled={busy}
              onClick={() =>
                lifetime.current && void loadAndResume(lifetime.current.signal)
              }
            >
              Retry loading recordings
            </button>
          ) : (
            "Loading recordings…"
          )}
        </p>
      ) : (
        <div className="cr-studio-grid">
          {!readOnly && (
            <section className="cr-panel">
              <h2>Create a recording</h2>
              {!studio.available && (
                <Notice error>
                  The voice generator is unavailable. Saved recordings still
                  work.{" "}
                  <button
                    className="cr-text-button"
                    disabled={busy}
                    onClick={() =>
                      lifetime.current &&
                      void loadAndResume(lifetime.current.signal)
                    }
                  >
                    Check again
                  </button>
                </Notice>
              )}
              <form className="cr-form" onSubmit={generate}>
                <fieldset
                  disabled={busy || Boolean(attempt)}
                  className="cr-recording-fields"
                  aria-label="Recording details"
                >
                  <Field title="Recording name">
                    <input
                      name="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                      maxLength={120}
                    />
                  </Field>
                  <div className="cr-form-grid">
                    <Field title="Use">
                      <select name="purpose">
                        <option value="voicemail">Voicemail greeting</option>
                        <option value="ivr">Phone menu (IVR)</option>
                        <option value="announcement">Announcement</option>
                      </select>
                    </Field>
                    {!accountId && (
                      <Field title="Customer account">
                        <select name="accountId" defaultValue="">
                          <option value="">Company recording</option>
                          {accounts.map((row) => (
                            <option key={row.id} value={row.id}>
                              {row.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                    )}
                  </div>
                  <Field title="Script">
                    <textarea
                      name="script"
                      value={script}
                      onChange={(e) => setScript(e.target.value)}
                      required
                      minLength={2}
                      maxLength={3000}
                      rows={9}
                    />
                  </Field>
                  <p className="cr-form-note">
                    {script.length.toLocaleString()} / 3,000 characters. Write
                    numbers as spoken words. Use punctuation for pauses.
                  </p>
                  <div className="cr-form-grid">
                    <Field title="Voice">
                      <select name="voice" defaultValue="af_heart">
                        {studio.voices.map((voice) => (
                          <option key={voice.id} value={voice.id}>
                            {voice.name} · {voice.description}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field title="Speaking speed">
                      <select name="speed" defaultValue="1">
                        <option value="0.85">Slower</option>
                        <option value="1">Normal</option>
                        <option value="1.15">Faster</option>
                      </select>
                    </Field>
                  </div>
                </fieldset>
                <button
                  className="cr-button"
                  disabled={busy || (!studio.available && !attempt)}
                >
                  {busy
                    ? "Generating recording…"
                    : attempt
                      ? "Check this attempt"
                      : "Generate & save recording"}
                </button>
                {attempt && !busy && (
                  <p role="status">
                    The last submission was not confirmed. “Check this attempt”
                    safely retries the same request without creating a
                    duplicate.
                  </p>
                )}
                {busy && (
                  <p role="status">
                    Your recording is being generated. You can leave this page
                    and return to check its progress.
                  </p>
                )}
                {lastJob?.status === "completed" && (
                  <p role="status">Recording saved. Listen before using it.</p>
                )}
              </form>
            </section>
          )}
          <div>
            <section className="cr-panel">
              <h2>Listen &amp; download</h2>
              {current ? (
                <>
                  <h3>{current.title}</h3>
                  <audio
                    className="cr-audio"
                    controls
                    preload="metadata"
                    key={current.id}
                    src={apiBase + base + "/" + current.id + "/audio"}
                  >
                    Your browser does not support audio playback. Use the
                    download link.
                  </audio>
                  <a
                    className="cr-button secondary"
                    href={apiBase + base + "/" + current.id + "/audio"}
                    download={current.title + ".wav"}
                  >
                    Download WAV
                  </a>
                  <p className="cr-form-note cr-audio-note">
                    8 kHz · mono · 16-bit PCM WAV
                  </p>
                </>
              ) : (
                <Empty title="No recording selected">
                  Choose a saved recording or create one.
                </Empty>
              )}
              <p className="cr-form-note">
                These are synthesized voices. Saving a recording does not change
                a live voicemail greeting or phone menu.
              </p>
            </section>
            <section className="cr-panel">
              <h2>Saved recordings</h2>
              {studio.prompts.length ? (
                <div className="cr-recordings">
                  {studio.prompts.map((row) => (
                    <button
                      key={row.id}
                      onClick={() => setCurrent(row)}
                      className={current?.id === row.id ? "selected" : ""}
                      aria-pressed={current?.id === row.id}
                    >
                      <strong>{row.title}</strong>
                      <span>
                        {label(row.purpose)} ·{" "}
                        {studio.voices.find((voice) => voice.id === row.voice)
                          ?.name ?? row.voice}
                      </span>
                      <small>{date(row.created_at)}</small>
                    </button>
                  ))}
                </div>
              ) : (
                <Empty title="No recordings yet">
                  Completed recordings for this{" "}
                  {accountId ? "account" : "company"} will appear here.
                </Empty>
              )}
              {studio.jobs.some(
                (job) =>
                  job.status === "failed" || job.status === "interrupted",
              ) && (
                <details>
                  <summary>Unfinished attempts</summary>
                  {studio.jobs
                    .filter(
                      (job) =>
                        job.status === "failed" || job.status === "interrupted",
                    )
                    .map((job) => (
                      <p key={job.id}>
                        <strong>{job.title}</strong> — {label(job.status)}. No
                        file saved.
                      </p>
                    ))}
                </details>
              )}
            </section>
          </div>
        </div>
      )}
    </>
  );
}

function Portal({
  session,
  onSession,
}: {
  session: Session;
  onSession: (value: Session) => void;
}) {
  const user = session.user!;
  const view = session.customerView;
  const admin = user.kind === "admin" && !view;
  const [section, setSection] = useState(
    () => readPortalRoute(location.search, admin).section,
  );
  const [accountTab, setAccountTab] = useState<AccountTab>(
    () => readPortalRoute(location.search, admin).tab,
  );
  const [data, setData] = useState<Overview | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [selected, setSelected] = useState<Row | null>(null);
  const [messages, setMessages] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [createdAccount, setCreatedAccount] = useState<{ id: string } | null>(
    null,
  );
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const newCustomerEditor = useRef<HTMLDetailsElement>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const tourButton = useRef<HTMLButtonElement>(null);
  const [numbersTab, setNumbersTab] = useState("owned");
  const [viewExpired, setViewExpired] = useState(Boolean(view?.expired));
  useEffect(() => {
    if (!view) return;
    const expire = () => {
      accountRequests.current.invalidate();
      setViewExpired(true);
      setData(null);
      setWorkspace(null);
      setMessages([]);
      setSelected(null);
    };
    const delay = new Date(view.expiresAt).getTime() - Date.now();
    if (view.expired || delay <= 0) {
      expire();
      return;
    }
    const timer = setTimeout(expire, delay);
    return () => clearTimeout(timer);
  }, [view]);
  const accountRequests = useRef(accountRequestGate());
  const ticketRequestKey = useRef(crypto.randomUUID());
  const replyRequestKeys = useRef(new Map<string, string>());
  useEffect(() => () => accountRequests.current.invalidate(), []);
  const load = useCallback(
    async (background = false) => {
      setBusy(false);
      const route = readPortalRoute(location.search, admin);
      if (view && route.section === "settings") route.section = "overview";
      const targetAccount = view
        ? route.section === "messages"
          ? null
          : view.accountId
        : route.accountId;
      let current = accountRequests.current.open(targetAccount ?? "page-load");
      if (!background) setLoading(true);
      setError("");
      if (viewExpired) {
        setLoading(false);
        return;
      }
      try {
        const overview = await boundedStatusRequest(
          request<Overview>(
            admin
              ? `/admin/overview?page=${new URLSearchParams(location.search).get("page") ?? "0"}`
              : "/customer/overview",
          ),
        );
        if (!current()) return;
        setData(overview);
        setRefreshVersion((value) => value + 1);
        setSelected((prior) =>
          prior && route.section === "inbox"
            ? (overview.requests.find((row) => row.id === prior.id) ?? null)
            : prior,
        );
        const accountToOpen =
          targetAccount ??
          (!admin &&
          route.section === "overview" &&
          overview.accounts.length === 1
            ? String(overview.accounts[0]!.id)
            : null);
        if (accountToOpen) {
          const next = await boundedStatusRequest(
            request<Workspace>(
              `/${admin ? "admin" : "customer"}/accounts/${accountToOpen}`,
            ),
          );
          if (!current()) return;
          current = accountRequests.current.open(accountToOpen);
          setWorkspace(next);
          setAccountTab(route.tab);
          setSection("account");
          if (view || !targetAccount)
            history.replaceState(
              null,
              "",
              portalRouteUrl(location.pathname, {
                section: "account",
                accountId: accountToOpen,
                tab: route.tab,
              }),
            );
        } else {
          setWorkspace(null);
          setSection(route.section);
        }
      } catch (cause) {
        if (current()) setError((cause as Error).message);
      } finally {
        if (current()) setLoading(false);
      }
    },
    [admin, viewExpired, view],
  );
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const restore = () => {
      setWorkspace(null);
      setSelected(null);
      setMessages([]);
      setInviteLink("");
      void load();
    };
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, [load]);
  useEffect(() => {
    document.title = `${admin ? "Admin" : "My account"} | Cache River`;
  }, [admin]);
  async function mutate(path: string, body: unknown) {
    return request<any>(path, {
      method: "POST",
      body,
      csrf: session.csrfToken,
    });
  }
  async function submit(
    event: FormEvent<HTMLFormElement>,
    path: string,
    success: string,
  ) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    const form = event.currentTarget;
    try {
      const values = Object.fromEntries(new FormData(form));
      const result = await mutate(
        path,
        path === "/customer/tickets"
          ? { ...values, requestKey: ticketRequestKey.current }
          : values,
      );
      if (path === "/customer/tickets")
        ticketRequestKey.current = crypto.randomUUID();
      if (result.invitationUrl) setInviteLink(result.invitationUrl);
      form.reset();
      await load();
      setNotice(success);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function writeRoute(route: PortalRoute) {
    const url = portalRouteUrl(location.pathname, route);
    if (`${location.pathname}${location.search}` !== url)
      history.pushState(null, "", url);
  }
  function navigate(next: string) {
    accountRequests.current.invalidate();
    setBusy(false);
    setMessages([]);
    setLoading(false);
    setSection(next);
    setWorkspace(null);
    setSelected(null);
    setError("");
    setNotice("");
    setInviteLink("");
    writeRoute({ section: next, accountId: null, tab: "overview" });
    if (
      refreshOverviewOnNavigate(next, admin) ||
      (admin && (data?.pagination?.page ?? 0) !== 0)
    ) {
      setData(null);
      void load();
    } else if (!data) void load();
  }
  async function openAccount(row: Row, tab: AccountTab = "overview") {
    const current = accountRequests.current.open(String(row.id));
    setLoading(true);
    setError("");
    try {
      const next = await boundedStatusRequest(
        request<Workspace>(
          `/${admin ? "admin" : "customer"}/accounts/${row.id}`,
        ),
      );
      if (!current()) return;
      setWorkspace(next);
      setSection("account");
      setAccountTab(tab);
      writeRoute({ section: "account", accountId: String(row.id), tab });
      return true;
    } catch (cause) {
      if (current()) setError((cause as Error).message);
      return false;
    } finally {
      if (current()) setLoading(false);
    }
  }
  async function refreshAccount() {
    if (!workspace) return;
    const current = accountRequests.current.refresh(
      String(workspace.account.id),
    );
    if (!current) return;
    try {
      const [nextWorkspace, nextOverview] = await boundedStatusRequest(
        Promise.all([
          request<Workspace>(
            `/${admin ? "admin" : "customer"}/accounts/${workspace.account.id}`,
          ),
          request<Overview>(admin ? "/admin/overview" : "/customer/overview"),
        ]),
      );
      if (!current()) return;
      setWorkspace(nextWorkspace);
      setData(nextOverview);
      setRefreshVersion((value) => value + 1);
      setNotice("Account records updated.");
    } catch (cause) {
      if (current()) throw cause;
    }
  }
  async function openTicket(row: Row) {
    const current = accountRequests.current.open(`ticket:${row.id}`);
    setBusy(true);
    setError("");
    try {
      const result = await request<{ items: Row[]; ticket: Row }>(
        `/tickets/${row.id}/messages`,
      );
      if (!current()) return;
      setMessages(result.items);
      setSelected({ ...row, ...result.ticket });
      setData((prior) =>
        prior
          ? {
              ...prior,
              tickets: prior.tickets.map((ticket) =>
                ticket.id === row.id ? { ...ticket, ...result.ticket } : ticket,
              ),
            }
          : prior,
      );
      return true;
    } catch (cause) {
      if (current()) setError((cause as Error).message);
    } finally {
      if (current()) setBusy(false);
    }
  }
  async function logout() {
    setBusy(true);
    try {
      await mutate("/logout", {});
      onSession({ user: null });
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const nav: [string, string][] = admin
    ? adminNavItems
    : [
        ["overview", "My accounts"],
        ["messages", "Messages"],
        ...(!view ? [["settings", "Password"] as [string, string]] : []),
      ];
  return (
    <div className="cr-portal">
      <a className="cr-skip" href="#portal-main">
        Skip to content
      </a>
      <header className="cr-portal-header">
        <Brand compact />
        <span>{admin ? "Administration" : "Customer portal"}</span>
        <div>
          <span>{user.displayName}</span>
          <button onClick={logout} disabled={busy}>
            Sign out
          </button>
        </div>
      </header>
      {view && (
        <section className="cr-customer-view" aria-label="Staff customer view">
          <div>
            <strong>Viewing {view.accountName} as a customer</strong>
            <p>
              {viewExpired
                ? "This view has expired."
                : `Read-only · Ends at ${new Date(view.expiresAt).toLocaleTimeString()}.`}{" "}
              Signed in as {user.displayName}.
            </p>
          </div>
          <button
            className="cr-button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                onSession(await mutate("/customer-view/exit", {}));
              } catch (cause) {
                setError((cause as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Exit customer view
          </button>
        </section>
      )}
      <div className="cr-portal-layout">
        <aside>
          <nav aria-label="Portal navigation">
            {admin ? (
              <AdminNavigation
                section={section}
                onNavigate={navigate}
                inquiries={
                  data?.requests.filter((r) => r.status === "new").length ?? 0
                }
              />
            ) : (
              nav.map(([key, title]) => (
                <button
                  key={key}
                  className={section === key ? "active" : ""}
                  aria-current={section === key ? "page" : undefined}
                  onClick={() => navigate(key)}
                >
                  {title}
                  {admin &&
                  key === "inbox" &&
                  data?.requests.filter((r) => r.status === "new").length ? (
                    <span>
                      {data.requests.filter((r) => r.status === "new").length}
                    </span>
                  ) : null}
                </button>
              ))
            )}
          </nav>
          <a className="cr-back-site" href={publicSite}>
            Company website
          </a>
        </aside>
        <main id="portal-main" className="cr-workspace">
          <div className="cr-workspace-heading">
            <div>
              <p className="cr-kicker">
                {admin ? "Cache River staff" : "Welcome back"}
              </p>
              <h1>
                {section === "account"
                  ? "Account workspace"
                  : (nav.find(([key]) => key === section)?.[1] ??
                    "Your account")}
              </h1>
            </div>
            <div className="cr-workspace-actions">
              <button
                ref={tourButton}
                className="cr-button secondary"
                disabled={loading || !data || viewExpired}
                aria-expanded={tourOpen}
                onClick={() => setTourOpen((value) => !value)}
              >
                Take a tour
              </button>
              <button
                className="cr-button secondary"
                onClick={async () => {
                  setRefreshing(true);
                  try {
                    if (section === "account" && workspace)
                      await refreshAccount();
                    else await load(true);
                  } catch (cause) {
                    setError((cause as Error).message);
                  } finally {
                    setRefreshing(false);
                  }
                }}
                disabled={loading || refreshing || busy}
              >
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
          {tourOpen && data && !viewExpired && (
            <PortalTour
              admin={admin}
              accounts={data.accounts.map((row) => ({
                id: String(row.id),
                name: String(row.name),
                account_number: String(row.account_number),
              }))}
              currentAccount={workspace ? String(workspace.account.id) : null}
              onNavigate={async (next, id, tab) => {
                if (next === "account" && id) await openAccount({ id }, tab);
                else navigate(next);
              }}
              onClose={() => {
                setTourOpen(false);
                tourButton.current?.focus();
              }}
            />
          )}
          {error && <Notice error>{error}</Notice>}
          {notice && <Notice>{notice}</Notice>}
          {admin && createdAccount && (
            <section className="cr-notice" aria-label="Saved customer">
              <p role="status">
                Customer saved. Opening the account does not create another
                customer.
              </p>
              <button
                className="cr-button secondary"
                disabled={loading}
                onClick={async () => {
                  if (await openAccount(createdAccount))
                    setCreatedAccount(null);
                }}
              >
                Open saved customer
              </button>{" "}
              <button
                className="cr-text-button"
                disabled={loading}
                onClick={() => setCreatedAccount(null)}
              >
                Dismiss saved result
              </button>
            </section>
          )}
          {loading && <p role="status">Loading your records…</p>}
          {!loading && data && !viewExpired && (
            <>
              {section === "carriers" && admin && (
                <CarriersPanel api={request} />
              )}
              {section === "feature-list" && admin && (
                <FeatureList onCustomers={() => navigate("customers")} />
              )}
              {section === "time-temperature" && admin && (
                <TimeTemperature api={request} csrf={session.csrfToken} />
              )}
              {section === "experiences" && admin && (
                <Experiences
                  api={request}
                  csrf={session.csrfToken}
                  accounts={data.accounts.map((account) => ({
                    id: String(account.id),
                    name: String(account.name),
                  }))}
                />
              )}
              {section === "voice" && admin && (
                <VoiceStudio
                  csrf={session.csrfToken}
                  accounts={data.accounts}
                />
              )}
              {section === "account" && workspace && (
                <AccountDetail
                  key={workspace.account.id}
                  workspace={workspace}
                  tab={accountTab}
                  onTab={(tab) => {
                    setAccountTab(tab);
                    writeRoute({
                      section: "account",
                      accountId: String(workspace.account.id),
                      tab,
                    });
                  }}
                  customer={!admin}
                  csrf={session.csrfToken}
                  onSaved={refreshAccount}
                  refreshVersion={refreshVersion}
                  readOnly={Boolean(view)}
                  onHelp={() => navigate("messages")}
                  {...(admin || data.accounts.length > 1
                    ? {
                        onBack: () =>
                          navigate(admin ? "customers" : "overview"),
                      }
                    : {})}
                  {...(admin
                    ? {
                        onCustomerView: async (reason: string) => {
                          onSession(
                            await mutate(
                              `/admin/accounts/${workspace.account.id}/customer-view`,
                              { reason },
                            ),
                          );
                        },
                      }
                    : {})}
                />
              )}
              {section === "service-status" && admin && (
                <ServiceStatusPanel
                  api={request}
                  refreshVersion={refreshVersion}
                />
              )}
              {section === "overview" && admin && (
                <AdminDashboard
                  api={request}
                  refreshVersion={refreshVersion}
                  onNavigate={navigate}
                  onAccount={(id, tab) => {
                    void openAccount({ id }, tab);
                  }}
                />
              )}
              {section === "inbox" && admin && (
                <>
                  <p className="cr-page-intro">
                    New service inquiries and requests from the website.
                  </p>
                  {selected ? (
                    <section className="cr-panel">
                      <button
                        className="cr-text-button"
                        onClick={() => {
                          accountRequests.current.invalidate();
                          setBusy(false);
                          setSelected(null);
                          setMessages([]);
                        }}
                      >
                        Back to inbox
                      </button>
                      <h2>{selected.name}</h2>
                      <dl className="cr-details">
                        <div>
                          <dt>Reference</dt>
                          <dd>{selected.reference}</dd>
                        </div>
                        <div>
                          <dt>Email</dt>
                          <dd>{selected.email}</dd>
                        </div>
                        <div>
                          <dt>Phone</dt>
                          <dd>{selected.phone || "Not supplied"}</dd>
                        </div>
                        <div>
                          <dt>Address</dt>
                          <dd>{selected.address || "Not supplied"}</dd>
                        </div>
                      </dl>
                      <p className="cr-message-body">{selected.message}</p>
                      <form
                        className="cr-inline-form"
                        onSubmit={(e) =>
                          submit(
                            e,
                            `/admin/requests/${selected.id}`,
                            "Request updated.",
                          )
                        }
                      >
                        <Field title="Request status">
                          <select name="status" defaultValue={selected.status}>
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="closed">Closed</option>
                          </select>
                        </Field>
                        <button className="cr-button" disabled={busy}>
                          Save status
                        </button>
                      </form>
                    </section>
                  ) : (
                    <Table
                      rows={data.requests}
                      columns={[
                        [
                          "name",
                          "From",
                          (r) => (
                            <button
                              className="cr-text-button"
                              onClick={() => setSelected(r)}
                            >
                              {r.name}
                            </button>
                          ),
                        ],
                        ["kind", "Regarding", (r) => label(r.kind)],
                        [
                          "status",
                          "Status",
                          (r) => <Status value={r.status} />,
                        ],
                        ["created_at", "Received", (r) => date(r.created_at)],
                      ]}
                      empty="Your inbox is clear"
                    />
                  )}
                </>
              )}
              {((section === "overview" && !admin) ||
                section === "customers") && (
                <>
                  {admin ? (
                    <CustomerDirectory
                      key={`directory:${data.pagination?.page ?? 0}`}
                      accounts={data.accounts.map((account) => ({
                        id: String(account.id),
                        name: String(account.name),
                        account_number: String(account.account_number),
                        kind: String(account.kind),
                        status: String(account.status),
                        contact_name: account.contact_name,
                        billing_email: account.billing_email,
                      }))}
                      page={data.pagination?.page ?? 0}
                      hasMore={data.pagination?.hasMore.accounts ?? false}
                      addDisabled={Boolean(createdAccount)}
                      onAccount={(id, tab) => void openAccount({ id }, tab)}
                      onAdd={() => {
                        setNewCustomerOpen(true);
                        requestAnimationFrame(() => {
                          newCustomerEditor.current?.scrollIntoView({
                            block: "nearest",
                          });
                          newCustomerEditor.current
                            ?.querySelector<HTMLInputElement>("input")
                            ?.focus();
                        });
                      }}
                    />
                  ) : (
                    <Table
                      rows={data.accounts}
                      columns={[
                        [
                          "name",
                          "Account",
                          (r) => (
                            <button
                              className="cr-text-button"
                              onClick={() => openAccount(r)}
                            >
                              {r.name}
                            </button>
                          ),
                        ],
                        ["account_number", "Account number"],
                        ["kind", "Type", (r) => label(r.kind)],
                        [
                          "status",
                          "Status",
                          (r) => <Status value={r.status} account />,
                        ],
                      ]}
                      empty={
                        admin
                          ? "No customer accounts yet"
                          : "No accounts linked to this sign in"
                      }
                    />
                  )}
                  {!admin && !data.accounts.length && (
                    <p>
                      Contact Cache River to have your account access checked.
                    </p>
                  )}
                  {admin && !createdAccount && (
                    <details
                      className="cr-panel cr-editor"
                      ref={newCustomerEditor}
                      open={newCustomerOpen}
                      onToggle={(event) =>
                        setNewCustomerOpen(event.currentTarget.open)
                      }
                    >
                      <summary>Add a customer</summary>
                      <NewCustomerForm
                        api={request}
                        csrf={session.csrfToken}
                        onCreated={async (account) => {
                          setCreatedAccount(account);
                          const opened = await openAccount(account, "numbers");
                          if (opened) setCreatedAccount(null);
                          try {
                            setData(await request<Overview>("/admin/overview"));
                          } catch {
                            setNotice(
                              "Customer saved. The customer list could not be refreshed; use Refresh to reload it.",
                            );
                          }
                        }}
                      />
                    </details>
                  )}
                </>
              )}
              {section === "orders" && (
                <>
                  <Notice>
                    Open an account to continue its setup. A saved order does
                    not confirm carrier activation or a working phone.
                  </Notice>
                  <Table
                    rows={data.orders}
                    columns={[
                      [
                        "order_number",
                        "Order",
                        (r) => (
                          <button
                            className="cr-text-button"
                            onClick={() =>
                              void openAccount({ id: r.account_id }, "orders")
                            }
                          >
                            {r.order_number}
                          </button>
                        ),
                      ],
                      ["account_name", "Account"],
                      ["type", "Work", (r) => label(r.type)],
                      ["status", "Status", (r) => <Status value={r.status} />],
                      [
                        "current_step",
                        "Next action",
                        (r) => label(r.current_step || "Review account"),
                      ],
                      ["updated_at", "Updated", (r) => date(r.updated_at)],
                    ]}
                    empty="No service orders recorded"
                  />
                </>
              )}
              {section === "numbers" && (
                <>
                  <nav className="cr-tabs" aria-label="Number tools">
                    {[
                      ["owned", "Your numbers"],
                      ["search", "Buy a new number"],
                    ].map(([key, title]) => (
                      <button
                        key={key}
                        className={numbersTab === key ? "selected" : ""}
                        aria-current={numbersTab === key ? "page" : undefined}
                        onClick={() => setNumbersTab(key!)}
                      >
                        {title}
                      </button>
                    ))}
                  </nav>
                  {numbersTab === "search" ? (
                    <AdminNumberPurchasePanel
                      api={request}
                      csrf={session.csrfToken}
                      accounts={data.accounts.map((row) => ({
                        id: String(row.id),
                        name: String(row.name),
                        account_number: String(row.account_number),
                      }))}
                      onSaved={async () => {
                        setData(await request<Overview>("/admin/overview"));
                      }}
                      onCustomers={() => navigate("customers")}
                      onAccount={(id) => {
                        void openAccount({ id }, "numbers");
                      }}
                    />
                  ) : (
                    <>
                      <OwnedNumbersPanel
                        api={request}
                        local={data.numbers as PortalNumber[]}
                        onRefresh={async () => {
                          setData(await request<Overview>("/admin/overview"));
                        }}
                        onAccount={(id) => {
                          void openAccount({ id }, "numbers");
                        }}
                      />
                      <details className="cr-panel cr-editor">
                        <summary>
                          Advanced: add verified inventory manually
                        </summary>
                        <InventoryForm
                          api={request}
                          csrf={session.csrfToken}
                          onSaved={load}
                        />
                      </details>
                    </>
                  )}
                </>
              )}
              {section === "ports" && admin && (
                <PortQueue
                  api={request}
                  onAccount={(row) => openAccount(row, "numbers")}
                />
              )}
              {section === "devices" && (
                <>
                  <p className="cr-page-intro">
                    Register equipment from the customer's account workspace.
                    Its MAC address identifies the device; it does not establish
                    a SIP registration.
                  </p>
                  <button
                    className="cr-button secondary"
                    onClick={() => navigate("customers")}
                  >
                    Choose a customer account
                  </button>
                  <Table
                    rows={data.devices}
                    columns={[
                      ["model", "Equipment"],
                      ["hardware_id", "Hardware ID"],
                      [
                        "account_name",
                        "Account",
                        (row) =>
                          row.account_id ? (
                            <button
                              className="cr-text-button"
                              onClick={() =>
                                void openAccount(
                                  { id: row.account_id },
                                  "devices",
                                )
                              }
                            >
                              {row.account_name || "Open customer"}
                            </button>
                          ) : (
                            "Not assigned"
                          ),
                      ],
                      [
                        "status",
                        "Recorded status",
                        (r) => <Status value={r.status} />,
                      ],
                    ]}
                    empty="No equipment recorded"
                  />
                </>
              )}
              {section === "plans" && (
                <PlansPanel
                  plans={data.plans}
                  api={request}
                  csrf={session.csrfToken}
                  onSaved={async () => {
                    setData(await request<Overview>("/admin/overview"));
                  }}
                />
              )}
              {section === "billing" && (
                <>
                  <StripeConnectionPanel api={request} />
                  <Table
                    rows={data.invoices}
                    columns={[
                      ["invoice_number", "Invoice"],
                      ["account_name", "Account"],
                      ["status", "Status", (r) => <Status value={r.status} />],
                      ["total_minor", "Total", (r) => money(r.total_minor)],
                      ["created_at", "Created", (r) => date(r.created_at)],
                    ]}
                    empty="No invoices issued"
                  />
                </>
              )}
              {section === "access" && (
                <>
                  <PortalAccessPanel
                    accounts={data.accounts}
                    api={request}
                    csrf={session.csrfToken}
                  />
                  <section className="cr-panel">
                    <h2>Invite an account holder</h2>
                    <p>
                      The invitation gives access only to the account selected
                      below, including its billing and extension setup. It is
                      account-wide access, not a restricted employee login. Send
                      it to the intended account holder privately.
                    </p>
                    <form
                      className="cr-form"
                      onSubmit={(e) =>
                        submit(
                          e,
                          "/admin/invitations",
                          "Invitation created. Copy the link and send it to the intended account holder.",
                        )
                      }
                    >
                      <Field title="Customer account">
                        <select name="accountId" required defaultValue="">
                          <option value="" disabled>
                            Select an account
                          </option>
                          {data.accounts.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name} · {r.account_number}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field title="Account holder email">
                        <input
                          name="email"
                          type="email"
                          required
                          maxLength={254}
                        />
                      </Field>
                      <button
                        className="cr-button"
                        disabled={busy || !data.accounts.length}
                      >
                        Create invitation link
                      </button>
                    </form>
                    {inviteLink && (
                      <div className="cr-invite">
                        <Field title="Private invitation link, valid for 24 hours">
                          <input
                            value={inviteLink}
                            readOnly
                            onFocus={(e) => e.currentTarget.select()}
                          />
                        </Field>
                        <button
                          className="cr-button secondary"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(inviteLink);
                              setNotice("Invitation link copied.");
                            } catch {
                              setNotice(
                                "Select the invitation link and copy it.",
                              );
                            }
                          }}
                        >
                          Copy invitation
                        </button>
                      </div>
                    )}
                  </section>
                </>
              )}
              {section === "messages" && (
                <>
                  {selected ? (
                    <section className="cr-panel">
                      <button
                        className="cr-text-button"
                        onClick={() => {
                          accountRequests.current.invalidate();
                          setBusy(false);
                          setSelected(null);
                          setMessages([]);
                        }}
                      >
                        Back to messages
                      </button>
                      <h2>{selected.subject}</h2>
                      <Status value={selected.status} />
                      <div className="cr-conversation">
                        {messages.map((r) => (
                          <article key={r.id}>
                            <header>
                              <strong>{r.author_name}</strong>
                              <time>{date(r.created_at)}</time>
                            </header>
                            <p className="cr-message-body">{r.body}</p>
                          </article>
                        ))}
                      </div>
                      {!view && (
                        <form
                          className="cr-form"
                          onSubmit={async (e) => {
                            e.preventDefault();
                            const form = e.currentTarget;
                            const current = accountRequests.current.open(
                              `reply:${selected.id}`,
                            );
                            setBusy(true);
                            setError("");
                            try {
                              if (!replyRequestKeys.current.has(selected.id))
                                replyRequestKeys.current.set(
                                  selected.id,
                                  crypto.randomUUID(),
                                );
                              await mutate(`/tickets/${selected.id}/messages`, {
                                ...Object.fromEntries(new FormData(form)),
                                requestKey: replyRequestKeys.current.get(
                                  selected.id,
                                ),
                              });
                              replyRequestKeys.current.delete(selected.id);
                              if (!current()) return;
                              form.reset();
                              if (await openTicket(selected))
                                setNotice("Reply sent.");
                            } catch (cause) {
                              if (current()) setError((cause as Error).message);
                            } finally {
                              if (current()) setBusy(false);
                            }
                          }}
                        >
                          <Field title="Your reply">
                            <textarea
                              name="body"
                              required
                              minLength={2}
                              maxLength={4000}
                              rows={4}
                            />
                          </Field>
                          {admin && (
                            <Field title="Status after reply">
                              <select
                                key={`${selected.id}:${selected.status}`}
                                name="status"
                                defaultValue={selected.status}
                              >
                                <option value="open">Open</option>
                                <option value="waiting_customer">
                                  Waiting for customer
                                </option>
                                <option value="closed">Closed</option>
                              </select>
                            </Field>
                          )}
                          <button className="cr-button" disabled={busy}>
                            Send reply
                          </button>
                        </form>
                      )}
                    </section>
                  ) : (
                    <>
                      <Table
                        rows={data.tickets}
                        columns={[
                          [
                            "subject",
                            "Subject",
                            (r) => (
                              <button
                                className="cr-text-button"
                                onClick={() => openTicket(r)}
                              >
                                {r.subject}
                              </button>
                            ),
                          ],
                          ["reference", "Reference"],
                          [
                            "status",
                            "Status",
                            (r) => <Status value={r.status} />,
                          ],
                          ["created_at", "Opened", (r) => date(r.created_at)],
                        ]}
                        empty="No account messages"
                      />
                      {!admin && !view && (
                        <section className="cr-panel">
                          <h2>Send Cache River a message</h2>
                          <form
                            className="cr-form"
                            onSubmit={(e) =>
                              submit(
                                e,
                                "/customer/tickets",
                                "Your message has been sent.",
                              )
                            }
                          >
                            <Field title="Account">
                              <select name="accountId" required defaultValue="">
                                <option value="" disabled>
                                  Select your account
                                </option>
                                {data.accounts.map((r) => (
                                  <option key={r.id} value={r.id}>
                                    {r.name}
                                  </option>
                                ))}
                              </select>
                            </Field>
                            <Field title="Subject">
                              <input
                                name="subject"
                                required
                                minLength={3}
                                maxLength={160}
                              />
                            </Field>
                            <Field title="Message">
                              <textarea
                                name="body"
                                required
                                minLength={10}
                                maxLength={4000}
                                rows={5}
                              />
                            </Field>
                            <p className="cr-form-note">
                              Keep passwords, payment details, and account PINs
                              out of messages. This inbox is not an emergency
                              service.
                            </p>
                            <button
                              className="cr-button"
                              disabled={busy || !data.accounts.length}
                            >
                              Send message
                            </button>
                          </form>
                        </section>
                      )}
                    </>
                  )}
                </>
              )}
              {admin &&
                data.pagination &&
                (
                  {
                    inbox: "requests",
                    messages: "tickets",
                    orders: "orders",
                    customers: "accounts",
                    access: "accounts",
                    devices: "devices",
                    billing: "invoices",
                  } as Record<string, string>
                )[section] && (
                  <nav
                    className="cr-record-pagination"
                    aria-label="Record pages"
                  >
                    <button
                      className="cr-button secondary"
                      disabled={loading || data.pagination.page === 0}
                      onClick={() => {
                        const url = new URL(location.href);
                        url.searchParams.set(
                          "page",
                          String(data.pagination!.page - 1),
                        );
                        history.pushState(null, "", url.pathname + url.search);
                        setSelected(null);
                        void load();
                      }}
                    >
                      Previous page
                    </button>
                    <span>Page {data.pagination.page + 1}</span>
                    <button
                      className="cr-button secondary"
                      disabled={
                        loading ||
                        !data.pagination.hasMore[
                          (
                            {
                              inbox: "requests",
                              messages: "tickets",
                              orders: "orders",
                              customers: "accounts",
                              access: "accounts",
                              devices: "devices",
                              billing: "invoices",
                            } as Record<string, string>
                          )[section]!
                        ]
                      }
                      onClick={() => {
                        const url = new URL(location.href);
                        url.searchParams.set(
                          "page",
                          String(data.pagination!.page + 1),
                        );
                        history.pushState(null, "", url.pathname + url.search);
                        setSelected(null);
                        void load();
                      }}
                    >
                      Next page
                    </button>
                  </nav>
                )}
              {section === "staff-email" && admin && (
                <StaffEmail api={request} mutate={mutate} />
              )}
              {section === "settings" && !view && (
                <>
                  {admin && (
                    <>
                      <PasskeySettings api={request} mutate={mutate} />
                      <MfaSettings api={request} mutate={mutate} />
                    </>
                  )}
                  {admin && (
                    <details className="cr-panel cr-editor">
                      <summary>Carrier connections</summary>
                      <CarriersPanel api={request} />
                    </details>
                  )}
                  <section className="cr-panel">
                    <h2>Change your password</h2>
                    <form
                      className="cr-form"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        setBusy(true);
                        setError("");
                        try {
                          await mutate(
                            "/change-password",
                            Object.fromEntries(new FormData(e.currentTarget)),
                          );
                          onSession({ user: null });
                        } catch (cause) {
                          setError((cause as Error).message);
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      <Field title="Current password">
                        <input
                          name="currentPassword"
                          type="password"
                          autoComplete="current-password"
                          required
                          maxLength={128}
                        />
                      </Field>
                      <Field title="New password, at least 15 characters">
                        <input
                          name="newPassword"
                          type="password"
                          autoComplete="new-password"
                          minLength={15}
                          maxLength={128}
                          required
                        />
                      </Field>
                      <p>
                        Changing your password signs out all current sessions.
                      </p>
                      <button className="cr-button" disabled={busy}>
                        Change password
                      </button>
                    </form>
                  </section>
                </>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default function LaunchApp() {
  const route = location.pathname.replace(/\/$/, "") || "/";
  const path =
    route === "/" && location.hostname === "portal.cacherivertelephone.com"
      ? "/customer"
      : route === "/" && location.hostname === "admin.cacherivertelephone.com"
        ? "/admin"
        : route;
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState("");
  const acceptSession = useCallback((next: Session) => {
    if (next.user && isCacheRiverHost(location.hostname)) {
      const destination = next.user.kind === "admin" ? staffSite : customerSite;
      if (location.origin !== destination) {
        location.assign(
          destination +
            (next.user.kind === "admin" ? "/admin" : "/customer?registered=1"),
        );
        return;
      }
    }
    sessionBoundary.changed();
    setError("");
    setSession(next);
  }, []);
  useEffect(() => {
    const expire = () => {
      acceptSession({ user: null });
      setError(
        "Your session has ended. Sign in again to continue. Unsaved changes were not submitted again.",
      );
    };
    window.addEventListener(sessionExpiredEvent, expire);
    return () => window.removeEventListener(sessionExpiredEvent, expire);
  }, [acceptSession]);
  const isPortal = [
    "/admin",
    "/customer",
    "/activate",
    "/register",
    "/recover",
  ].includes(path);
  useEffect(() => {
    if (!isPortal || path === "/activate" || path === "/recover") return;
    if (hostedCopy) {
      location.replace(portalLink(path + location.search));
      return;
    }
    request<Session>("/session")
      .then(acceptSession)
      .catch((cause) => {
        setError((cause as Error).message);
        setSession({ user: null });
      });
  }, [isPortal, path, acceptSession]);
  useEffect(() => {
    const mailAuth = new URLSearchParams(location.search).get("mail_auth");
    if (session?.user?.kind === "admin" && !session.customerView && mailAuth) {
      location.replace(
        "/v1/launch/staff-mail-sso/authorize?" +
          new URLSearchParams(mailAuth).toString(),
      );
    }
  }, [session]);
  if (path === "/privacy" || path === "/website-use")
    return <Policy privacy={path === "/privacy"} />;
  if (!isPortal) return <Home />;
  if (path === "/recover") return <RecoverPassword />;
  if (session?.user)
    return (
      <Portal
        key={session.customerView?.id ?? session.user.id}
        session={session}
        onSession={acceptSession}
      />
    );
  if (path === "/activate")
    return <AcceptInvitation onSession={acceptSession} />;
  if (path === "/register" && !hostedCopy)
    return <Registration onSession={acceptSession} />;
  if (!session)
    return (
      <div className="cr-auth">
        <header>
          <Brand />
        </header>
        <main>
          <p role="status">Checking your account session…</p>
        </main>
      </div>
    );
  return (
    <>
      {error && <Notice error>{error}</Notice>}
      <Login admin={path === "/admin"} onSession={acceptSession} />
    </>
  );
}
